import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs, Spin, message, Pagination, Button } from 'antd';
import type { TabsProps } from 'antd';
import { Prospect } from 'types/common';
import { getTreatmentPlanAppointmentCounts, getTreatmentPlanItemList } from 'api/home';
import { getGPTreatmentPlanCounts, getGPTreatmentPlanItemList } from 'api/group';
import { TTreatmentPlanAppointmentQueryReq, TreatmentPlanItemVo } from 'types/home';
import { TextVisitServiceType, TPrescriptionListItem } from 'types/textVisit';
import TreatmentPlanCard from './TreatmentPlanCard';
import VideoVisitUnSubmittedDrawer from 'components/VideoVisitUnSubmittedDrawer';
import VideoVisitSubmittedDrawer from 'components/VideoVisitSubmittedDrawer';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import s from './s.module.less';
import IntakeFormDrawer from 'components/IntakeFormDrawer';
import useTreatmentAppointmentDetail from 'hooks/treatment/useTreatmentAppointmentDetail';
import useGetCaseDetail from 'hooks/textVisit/useGetCaseDetail';
import DiagnosisCodesModal from './components/DiagnosisCodesModal';
import CreateVisitNoteModal, { CreateVisitNoteResult } from './components/CreateVisitNoteModal';
import NonAppointmentUnSubmittedVisitNoteDrawer from 'components/NonAppointmentUnSubmittedVisitNoteDrawer';
import NonAppointmentSubmittedVisitNoteDrawer from 'components/NonAppointmentSubmittedVisitNoteDrawer';

type Props = {
    prospect?: Prospect;
    isGroupPractice?: boolean;
    useForModule?: boolean;
};

type TreatmentPlanStatus = 'IN_PROGRESS' | 'OVERDUE' | 'SENT';
// Text Visit 服务类型映射
const serviceTypeMap = {
    [TextVisitServiceType.MEDICATION_REFILL]: {
        title: 'medication refill',
    },
    [TextVisitServiceType.PRESCRIPTION_REFILL]: {
        title: 'prescription refill',
    },
    [TextVisitServiceType.MEDICATION_CHANGE]: {
        title: 'Medication change',
    },
    [TextVisitServiceType.FOLLOW_UP_CONSULTATION]: {
        title: 'Follow-up consultation',
    },
    [TextVisitServiceType.OTHER]: {
        title: 'Other care needs',
    },
};

const TreatmentPlan = ({ prospect, isGroupPractice, useForModule }: Props) => {
    const [activeTab, setActiveTab] = useState<TreatmentPlanStatus>('IN_PROGRESS');
    const [loading, setLoading] = useState<Record<TreatmentPlanStatus, boolean>>({
        IN_PROGRESS: false,
        OVERDUE: false,
        SENT: false,
    });
    const [items, setItems] = useState<Record<TreatmentPlanStatus, TreatmentPlanItemVo[]>>({
        IN_PROGRESS: [],
        OVERDUE: [],
        SENT: [],
    });
    const [counts, setCounts] = useState<Record<TreatmentPlanStatus, number>>({
        IN_PROGRESS: 0,
        OVERDUE: 0,
        SENT: 0,
    });
    const [pages, setPages] = useState<Record<TreatmentPlanStatus, number>>({
        IN_PROGRESS: 1,
        OVERDUE: 1,
        SENT: 1,
    });
    const [showVideoVisitUnSubmittedDrawer, setShowVideoVisitUnSubmittedDrawer] = useState(false);
    const [showTextVisitUnSubmittedDrawer, setShowTextVisitUnSubmittedDrawer] = useState(false);
    const [showTextVisitSubmittedDrawer, setShowTextVisitSubmittedDrawer] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<number>();
    const [selectedTextVisitUuid, setSelectedTextVisitUuid] = useState<string>();
    const [showVideoVisitSubmittedDrawer, setShowVideoVisitSubmittedDrawer] = useState(false);
    const [showNonAppointmentUnSubmittedVisitNoteDrawer, setShowNonAppointmentUnSubmittedVisitNoteDrawer] = useState(false);
    const [showNonAppointmentSubmittedVisitNoteDrawer, setShowNonAppointmentSubmittedVisitNoteDrawer] = useState(false);
    const [nonAppointmentSpecialty, setNonAppointmentSpecialty] = useState<string | undefined>();
    const [nonAppointmentNoteId, setNonAppointmentNoteId] = useState<number | undefined>();
    const [showIntakeFormDrawer, setShowIntakeFormDrawer] = useState(false);
    const [currentItemType, setCurrentItemType] = useState<'TEXT_VISIT' | 'APPOINTMENT'>();
    const [currentTextvisitId, setCurrentTextvisitId] = useState<string>();
    const [textVisitDetail] = useGetCaseDetail({
        caseUuid: currentTextvisitId,
    });
    const [currentAppointmentId, setCurrentAppointmentId] = useState<number>();
    const [appointmentTreatmentDetail] = useTreatmentAppointmentDetail(currentAppointmentId);
    const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
    const [showCreateVisitNoteModal, setShowCreateVisitNoteModal] = useState(false);
    const pageSize = 10;
    const clientId = isGroupPractice ? prospect?.identityId : prospect?.id;
    const mergedInProgressRef = useRef<TreatmentPlanItemVo[]>([]);

    const fetchCounts = useCallback(async () => {
        if (!clientId) {
            return;
        }

        try {
            const result = isGroupPractice
                ? await getGPTreatmentPlanCounts(clientId)
                : await getTreatmentPlanAppointmentCounts(clientId);
            if (!result.error && result.data?.data) {
                setCounts({
                    IN_PROGRESS: result.data.data.inProgressCount,
                    OVERDUE: result.data.data.overdueCount,
                    SENT: result.data.data.sentCount,
                });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch counts:', error);
        }
    }, [clientId, isGroupPractice]);

    const getItemKey = useCallback((item: TreatmentPlanItemVo): string => {
        if (item.itemType === 'TEXT_VISIT' && item.textVisit) return `tv-${item.textVisit.id}`;
        if (item.itemType === 'APPOINTMENT' && item.appointment) return `apt-${item.appointment.id}`;
        if (item.itemType === 'STANDALONE' && item.standaloneNote) return `sa-${item.standaloneNote.id}`;
        return '';
    }, []);

    const fetchItems = useCallback(
        async (status: TreatmentPlanStatus, page: number = 1) => {
            if (!clientId) {
                return;
            }

            setLoading((prev) => ({ ...prev, [status]: true }));
            try {
                if (status === 'IN_PROGRESS') {
                    const baseParams: TTreatmentPlanAppointmentQueryReq = {
                        clientId,
                        orderKey: 'START_AT',
                        orderType: 'ASC',
                        current: 1,
                        size: 100,
                    };
                    const [resInProgress, resUpcoming] = await Promise.all([
                        isGroupPractice
                            ? getGPTreatmentPlanItemList({ ...baseParams, status: 'IN_PROGRESS' })
                            : getTreatmentPlanItemList({ ...baseParams, status: 'IN_PROGRESS' }),
                        isGroupPractice
                            ? getGPTreatmentPlanItemList({ ...baseParams, status: 'UPCOMING' })
                            : getTreatmentPlanItemList({ ...baseParams, status: 'UPCOMING' }),
                    ]);
                    const list1 = resInProgress?.data?.data?.list || [];
                    const list2 = (resUpcoming?.data?.data?.list || []).filter(
                        (item: TreatmentPlanItemVo) => item.itemType === 'APPOINTMENT',
                    );

                    const seen = new Set<string>();
                    const merged: TreatmentPlanItemVo[] = [];
                    for (const item of [...list1, ...list2]) {
                        const key = getItemKey(item);
                        if (key && !seen.has(key)) {
                            seen.add(key);
                            merged.push(item);
                        }
                    }
                    merged.sort((a, b) => {
                        const dateA = a.sortCreatedAt || a.sortStartAt || '';
                        const dateB = b.sortCreatedAt || b.sortStartAt || '';
                        return dateB.localeCompare(dateA);
                    });

                    mergedInProgressRef.current = merged;
                    const start = (page - 1) * pageSize;
                    setItems((prev) => ({ ...prev, IN_PROGRESS: merged.slice(start, start + pageSize) }));
                    setCounts((prev) => ({ ...prev, IN_PROGRESS: merged.length }));
                } else {
                    const params: TTreatmentPlanAppointmentQueryReq = {
                        clientId,
                        status,
                        orderKey: 'START_AT',
                        orderType: status === 'SENT' ? 'DESC' : 'ASC',
                        current: page,
                        size: pageSize,
                    };

                    const res = isGroupPractice
                        ? await getGPTreatmentPlanItemList(params)
                        : await getTreatmentPlanItemList(params);
                    const pageData = res?.data?.data;
                    const itemsList = pageData?.list || [];
                    setItems((prev) => ({ ...prev, [status]: itemsList }));
                    if (pageData?.totalRecords !== undefined) {
                        setCounts((prev) => ({ ...prev, [status]: pageData.totalRecords }));
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Failed to fetch ${status} items:`, error);
                message.error('Failed to fetch visit notes items');
                setItems((prev) => ({ ...prev, [status]: [] }));
            } finally {
                setLoading((prev) => ({ ...prev, [status]: false }));
            }
        },
        [clientId, pageSize, isGroupPractice, getItemKey],
    );

    useEffect(() => {
        if (clientId) {
            fetchCounts();
            fetchItems(activeTab, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const handleButtonClick = useCallback((item: TreatmentPlanItemVo) => {
        if (item.itemType === 'STANDALONE') {
            const { standaloneNote } = item;
            if (standaloneNote) {
                setNonAppointmentSpecialty(standaloneNote.specialty || undefined);
                setNonAppointmentNoteId(standaloneNote.id);
                if (standaloneNote.treatmentSubmittedAt) {
                    setShowNonAppointmentSubmittedVisitNoteDrawer(true);
                } else {
                    setShowNonAppointmentUnSubmittedVisitNoteDrawer(true);
                }
            }
        } else if (item.itemType === 'TEXT_VISIT') {
            const { textVisit } = item;
            if (textVisit?.textVisitUuid) {
                setSelectedTextVisitUuid(textVisit.textVisitUuid);
                if (textVisit.providerTreatmentSubmitted) {
                    setShowTextVisitSubmittedDrawer(true);
                } else {
                    setShowTextVisitUnSubmittedDrawer(true);
                }
            }
        } else if (item.itemType === 'APPOINTMENT') {
            const { appointment } = item;
            if (appointment?.id) {
                setSelectedAppointmentId(appointment.id);
                if (appointment.providerTreatmentSubmitted) {
                    setShowVideoVisitSubmittedDrawer(true);
                } else {
                    setShowVideoVisitUnSubmittedDrawer(true);
                }
            }
        }
    }, []);

    const handleCreateVisitNoteContinue = useCallback((result: CreateVisitNoteResult) => {
        if (result.itemType === 'STANDALONE' && result.standaloneNoteId) {
            setNonAppointmentSpecialty(result.specialty);
            setNonAppointmentNoteId(result.standaloneNoteId);
            setShowNonAppointmentUnSubmittedVisitNoteDrawer(true);
        } else if (result.appointmentId || result.textVisitUuid) {
            if (result.itemType === 'APPOINTMENT' && result.appointmentId) {
                setSelectedAppointmentId(result.appointmentId);
                if (result.providerTreatmentSubmitted) {
                    setShowVideoVisitSubmittedDrawer(true);
                } else {
                    setShowVideoVisitUnSubmittedDrawer(true);
                }
            } else if (result.itemType === 'TEXT_VISIT' && result.textVisitUuid) {
                setSelectedTextVisitUuid(result.textVisitUuid);
                if (result.providerTreatmentSubmitted) {
                    setShowTextVisitSubmittedDrawer(true);
                } else {
                    setShowTextVisitUnSubmittedDrawer(true);
                }
            }
        }
    }, []);

    const handleViewIntake = (item: TreatmentPlanItemVo) => {
        setCurrentItemType(item.itemType);
        if (item.itemType === 'TEXT_VISIT') {
            setCurrentTextvisitId(item.textVisit?.textVisitUuid);
        } else if (item.itemType === 'APPOINTMENT') {
            setCurrentAppointmentId(item.appointment?.id);
        }
        setShowIntakeFormDrawer(true);
    };

    const handlePageChange = useCallback(
        (page: number, status: TreatmentPlanStatus) => {
            setPages((prev) => ({ ...prev, [status]: page }));
            if (status === 'IN_PROGRESS' && mergedInProgressRef.current.length > 0) {
                const start = (page - 1) * pageSize;
                setItems((prev) => ({
                    ...prev,
                    IN_PROGRESS: mergedInProgressRef.current.slice(start, start + pageSize),
                }));
            } else {
                fetchItems(status, page);
            }
        },
        [fetchItems, pageSize],
    );

    const renderAppointmentList = useCallback(
        (status: TreatmentPlanStatus) => {
            const itemsList = items[status];
            const isLoading = loading[status];
            const currentPage = pages[status];
            const total = counts[status];

            if (isLoading) {
                return (
                    <div className={s.loadingContainer}>
                        <Spin />
                    </div>
                );
            }

            if (itemsList.length === 0) {
                return <div className={s.empty}>No {status.toLowerCase().replace('_', '-')} visit notes</div>;
            }

            return (
                <div className={s.listContainer}>
                    <div className={s.appointmentList}>
                        {itemsList.map((item) => {
                            if (item.itemType === 'STANDALONE') {
                                const { standaloneNote } = item;
                                if (!standaloneNote) return null;

                                const appointmentName = standaloneNote.specialty
                                    ? `${standaloneNote.specialty}: Visit note`
                                    : 'Visit note';

                                return (
                                    <TreatmentPlanCard
                                        key={`standalone-${standaloneNote.id}`}
                                        appointmentName={appointmentName}
                                        startAt={standaloneNote.createdAt}
                                        endAt={standaloneNote.createdAt}
                                        duration={0}
                                        hasTextVisitTreatment={!!standaloneNote.treatmentPlan}
                                        treatmentPlanText={standaloneNote.treatmentPlan}
                                        attachmentsCount={standaloneNote.attachmentCount || 0}
                                        submittedAt={standaloneNote.treatmentSubmittedAt || undefined}
                                        lastUpdatedAt={standaloneNote.updatedAt}
                                        status={status}
                                        isDismissed={false}
                                        itemType="STANDALONE"
                                        isRefillVisit={false}
                                        onButtonClick={() => handleButtonClick(item)}
                                        providerName={
                                            isGroupPractice && item.provider
                                                ? `${item.provider.firstName || ''} ${item.provider.lastName || ''}`.trim()
                                                : undefined
                                        }
                                        useForModule={useForModule}
                                    />
                                );
                            }
                            if (item.itemType === 'TEXT_VISIT') {
                                const { textVisit } = item;
                                if (!textVisit) return null;

                                const isFollowUpCase = textVisit.serviceType !== TextVisitServiceType.INITIAL_VISIT;
                                const conditionName = isFollowUpCase
                                    ? serviceTypeMap[textVisit.serviceType as keyof typeof serviceTypeMap]?.title
                                    : textVisit.conditions;
                                const isRefillVisit =
                                    textVisit.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
                                    textVisit.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

                                const baseName = `${conditionName}: Text visit`;
                                const appointmentName =
                                    isRefillVisit && textVisit.specialty
                                        ? `${textVisit.specialty} ${baseName}`
                                        : baseName;
                                const startAt = textVisit.assignedProviderAt || textVisit.createdAt;
                                // endAt 设置为 assignedProviderAt 的 15 天后
                                const baseDate = textVisit.assignedProviderAt || textVisit.createdAt;
                                const endAt = baseDate
                                    ? new Date(new Date(baseDate).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString()
                                    : textVisit.createdAt;
                                const duration = 0;
                                const hasTextVisitTreatment = textVisit.hasTextVisitTreatment || false;
                                const treatmentPlanText = textVisit.textVisitTreatmentVo?.treatmentPlan;
                                const attachmentsCount = textVisit.attachmentCount || 0;
                                const prescriptions =
                                    textVisit.prescriptions && Array.isArray(textVisit.prescriptions)
                                        ? (textVisit.prescriptions as TPrescriptionListItem[])
                                        : undefined;

                                return (
                                    <TreatmentPlanCard
                                        key={`textVisit-${textVisit.id}`}
                                        appointmentName={appointmentName}
                                        startAt={startAt}
                                        endAt={endAt}
                                        duration={duration}
                                        hasTextVisitTreatment={hasTextVisitTreatment}
                                        treatmentPlanText={treatmentPlanText}
                                        attachmentsCount={attachmentsCount}
                                        prescriptions={prescriptions}
                                        submittedAt={textVisit.assignedProviderAt || undefined}
                                        lastUpdatedAt={textVisit.updatedAt}
                                        status={status}
                                        isDismissed={false}
                                        itemType="TEXT_VISIT"
                                        isRefillVisit={isRefillVisit}
                                        onButtonClick={() => handleButtonClick(item)}
                                        onClickViewIntake={() => handleViewIntake(item)}
                                        providerName={
                                            isGroupPractice && item.provider
                                                ? `${item.provider.firstName || ''} ${item.provider.lastName || ''}`.trim()
                                                : undefined
                                        }
                                        useForModule={useForModule}
                                    />
                                );
                            } else {
                                const { appointment } = item;
                                if (!appointment) return null;

                                const treatmentPlanText = appointment.textVisitTreatmentVo
                                    ? (appointment.textVisitTreatmentVo as { treatmentPlan?: string })?.treatmentPlan
                                    : undefined;
                                const attachmentsCount = appointment.attachmentCount || 0;
                                const prescriptions =
                                    appointment.prescriptions && Array.isArray(appointment.prescriptions)
                                        ? (appointment.prescriptions as TPrescriptionListItem[])
                                        : undefined;
                                const isRefillVisit =
                                    appointment.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
                                    appointment.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

                                return (
                                    <TreatmentPlanCard
                                        key={`appointment-${appointment.id}`}
                                        appointmentName={appointment.appointmentName}
                                        startAt={appointment.startAt}
                                        endAt={appointment.endAt}
                                        duration={appointment.duration}
                                        hasTextVisitTreatment={appointment.hasTextVisitTreatment || false}
                                        treatmentPlanText={treatmentPlanText}
                                        attachmentsCount={attachmentsCount}
                                        prescriptions={prescriptions}
                                        status={status}
                                        isDismissed={false}
                                        itemType="APPOINTMENT"
                                        isRefillVisit={isRefillVisit}
                                        onButtonClick={() => handleButtonClick(item)}
                                        onClickViewIntake={() => handleViewIntake(item)}
                                        providerName={
                                            isGroupPractice && item.provider
                                                ? `${item.provider.firstName || ''} ${item.provider.lastName || ''}`.trim()
                                                : undefined
                                        }
                                        useForModule={useForModule}
                                    />
                                );
                            }
                        })}
                    </div>
                    {total > pageSize && (
                        <div className={s.pagination}>
                            <Pagination
                                current={currentPage}
                                total={total}
                                pageSize={pageSize}
                                onChange={(page) => handlePageChange(page, status)}
                                showSizeChanger={false}
                                showQuickJumper={false}
                            />
                        </div>
                    )}
                </div>
            );
        },
        [items, loading, pages, counts, handleButtonClick, handlePageChange, isGroupPractice, useForModule],
    );

    const allTabItems: (TabsProps['items'][0] & { status: TreatmentPlanStatus })[] = [
        {
            key: 'IN_PROGRESS',
            status: 'IN_PROGRESS' as TreatmentPlanStatus,
            label: (
                <div>
                    In-progress <span className={s.count}>({counts.IN_PROGRESS})</span>
                </div>
            ),
            children: (
                <div key={`IN_PROGRESS-${loading.IN_PROGRESS}-${items.IN_PROGRESS.length}`}>
                    {renderAppointmentList('IN_PROGRESS')}
                </div>
            ),
        },
        {
            key: 'OVERDUE',
            status: 'OVERDUE' as TreatmentPlanStatus,
            label: (
                <div>
                    Overdue <span className={s.count}>({counts.OVERDUE})</span>
                </div>
            ),
            children: (
                <div key={`OVERDUE-${loading.OVERDUE}-${items.OVERDUE.length}`}>{renderAppointmentList('OVERDUE')}</div>
            ),
        },
        {
            key: 'SENT',
            status: 'SENT' as TreatmentPlanStatus,
            label: (
                <div>
                    Sent <span className={s.count}>({counts.SENT})</span>
                </div>
            ),
            children: <div key={`SENT-${loading.SENT}-${items.SENT.length}`}>{renderAppointmentList('SENT')}</div>,
        },
    ];

    const tabItems = useForModule
        ? allTabItems.filter((tab) => counts[tab.status] > 0)
        : allTabItems;

    useEffect(() => {
        if (clientId) {
            setPages({
                IN_PROGRESS: 1,
                OVERDUE: 1,
                SENT: 1,
            });
        }
    }, [clientId]);

    useEffect(() => {
        if (!useForModule) return;
        const statuses: TreatmentPlanStatus[] = ['IN_PROGRESS', 'OVERDUE', 'SENT'];
        const firstNonEmpty = statuses.find((st) => counts[st] > 0);
        if (firstNonEmpty && counts[activeTab] === 0) {
            setActiveTab(firstNonEmpty);
            fetchItems(firstNonEmpty, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counts, useForModule]);

    const handleTabChange = useCallback(
        (key: string) => {
            const newStatus = key as TreatmentPlanStatus;
            setActiveTab(newStatus);
            setPages((prev) => ({ ...prev, [newStatus]: 1 }));
            setLoading((prev) => ({ ...prev, [newStatus]: true }));
            setItems((prev) => ({ ...prev, [newStatus]: [] }));
            fetchItems(newStatus, 1);
        },
        [fetchItems],
    );

    return (
        <>
            <div className={`${s.wrap} ${useForModule ? s.wrapModule : ''}`}>
                {useForModule && tabItems.length === 0 ? (
                    <div className={s.empty}>No visit notes</div>
                ) : (
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                        destroyInactiveTabPane
                        tabBarExtraContent={
                            !useForModule ? (
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {!isGroupPractice && (
                                        <>
                                            <Button onClick={() => setShowDiagnosisModal(true)}>Diagnosis codes</Button>
                                            <Button type="primary" onClick={() => setShowCreateVisitNoteModal(true)}>
                                                Create visit note
                                            </Button>
                                        </>
                                    )}
                                </div>
                            ) : undefined
                        }
                    />
                )}
            </div>
            {showVideoVisitUnSubmittedDrawer && (
                <VideoVisitUnSubmittedDrawer
                    show={showVideoVisitUnSubmittedDrawer}
                    readOnly={isGroupPractice}
                    onClose={() => {
                        setShowVideoVisitUnSubmittedDrawer(false);
                        setSelectedAppointmentId(undefined);
                    }}
                    appointmentId={selectedAppointmentId}
                    onSave={() => {
                        fetchCounts();
                        fetchItems(activeTab, pages[activeTab]);
                    }}
                />
            )}
            {showVideoVisitSubmittedDrawer && (
                <VideoVisitSubmittedDrawer
                    show={showVideoVisitSubmittedDrawer}
                    onClose={() => {
                        setShowVideoVisitSubmittedDrawer(false);
                        setSelectedAppointmentId(undefined);
                    }}
                    appointmentId={selectedAppointmentId}
                />
            )}
            {showTextVisitUnSubmittedDrawer && (
                <TextVisitUnSubmittedDrawer
                    show={showTextVisitUnSubmittedDrawer}
                    fullyWithSaveFun
                    readOnly={isGroupPractice}
                    onClose={() => {
                        setShowTextVisitUnSubmittedDrawer(false);
                        setSelectedTextVisitUuid(undefined);
                    }}
                    textVisitUuid={selectedTextVisitUuid}
                    onSave={() => {
                        fetchCounts();
                        fetchItems(activeTab, pages[activeTab]);
                    }}
                    onNext={(data) => {
                        const { nextTextVisitUuid, inProgress } = data;
                        if (nextTextVisitUuid) {
                            setSelectedTextVisitUuid(nextTextVisitUuid);
                            if (!inProgress) {
                                setShowTextVisitUnSubmittedDrawer(false);
                            }
                        }
                    }}
                />
            )}
            {showTextVisitSubmittedDrawer && (
                <TextVisitSubmittedDrawer
                    open={showTextVisitSubmittedDrawer}
                    onClose={() => {
                        setShowTextVisitSubmittedDrawer(false);
                        setSelectedTextVisitUuid(undefined);
                    }}
                    textVisitUuid={selectedTextVisitUuid}
                    isGroupPractice={isGroupPractice}
                />
            )}
            <IntakeFormDrawer
                show={showIntakeFormDrawer}
                onClose={() => {
                    setShowIntakeFormDrawer(false);
                }}
                textVisitDetail={currentItemType === 'TEXT_VISIT' ? textVisitDetail : undefined}
                appointmentTreatmentDetail={currentItemType === 'APPOINTMENT' ? appointmentTreatmentDetail : undefined}
            />
            <DiagnosisCodesModal
                open={showDiagnosisModal}
                onClose={() => setShowDiagnosisModal(false)}
                prospectId={prospect?.id}
            />
            <CreateVisitNoteModal
                open={showCreateVisitNoteModal}
                onClose={() => setShowCreateVisitNoteModal(false)}
                onContinue={handleCreateVisitNoteContinue}
                prospect={prospect}
            />
            {showNonAppointmentSubmittedVisitNoteDrawer && (
                <NonAppointmentSubmittedVisitNoteDrawer
                    show={showNonAppointmentSubmittedVisitNoteDrawer}
                    onClose={() => {
                        setShowNonAppointmentSubmittedVisitNoteDrawer(false);
                        setNonAppointmentSpecialty(undefined);
                        setNonAppointmentNoteId(undefined);
                    }}
                    noteId={nonAppointmentNoteId}
                    specialty={nonAppointmentSpecialty}
                />
            )}
            {showNonAppointmentUnSubmittedVisitNoteDrawer && (
                <NonAppointmentUnSubmittedVisitNoteDrawer
                    show={showNonAppointmentUnSubmittedVisitNoteDrawer}
                    onClose={() => {
                        setShowNonAppointmentUnSubmittedVisitNoteDrawer(false);
                        setNonAppointmentSpecialty(undefined);
                        setNonAppointmentNoteId(undefined);
                    }}
                    clientId={clientId}
                    specialty={nonAppointmentSpecialty}
                    noteId={nonAppointmentNoteId}
                    isGroupPractice={isGroupPractice}
                    onSave={() => {
                        fetchCounts();
                        fetchItems(activeTab, pages[activeTab]);
                    }}
                />
            )}
        </>
    );
};

export default TreatmentPlan;
